import { Injectable } from '@angular/core'

import {
  ChallengeDto,
  MilestoneType,
  RewardDiscountType,
  RewardType,
  TranslationDto
} from '../../dto'

import { AdminChallengeService } from './admin-challenge.service'

/** Thông tin Milestone cần join thêm cho API #17, #18, #21, #22. */
export interface MilestoneIndexEntry {
  id?: string
  /** `ChallengeMilestone.position` — FE dựng mã MS-00x. */
  position?: number
  /** Quyết định gọi API #15 (QR tĩnh) hay API #16 (QR động). */
  type?: MilestoneType
  name?: TranslationDto
  /**
   * QR tĩnh có khả dụng không. Nguồn duy nhất cho Milestone **chưa phát tem** — API #21
   * không trả dòng cho những Milestone đó.
   */
  qrAvailable?: boolean
  /** Check-in Event — QR của Milestone chính là QR của Event (Customer FR-05-BR-05). */
  eventId?: string
}

/** Thông tin Reward Option cần join thêm cho API #4, #8. */
export interface RewardOptionIndexEntry {
  id?: string
  tierPosition?: number
  type?: RewardType
  /** Cần để format cột Discount (% hay $) — API #4 không trả. */
  discountType?: RewardDiscountType
  name?: TranslationDto
  numberOfRewards?: number
  /** Phạm vi cấu hình — fallback cho cột Mall / Tenant / Discount khi code chưa dùng (API #8). */
  mallIds?: string[]
  offlineTenantIds?: string[]
  discountValue?: number
}

/**
 * Lớp join dữ liệu còn thiếu của các API Stamp Collection.
 *
 * BE trả `milestoneId` / `rewardOptionId` nhưng **không** trả kèm `position`, `type`,
 * `discountType` — những giá trị UI cần để render mã MS-00x, chọn đúng API QR, và format
 * cột Discount. Cả 3 đều có trong API #10 (Challenge Detail), nên service này gọi API #10
 * một lần rồi cache theo `challengeId`.
 *
 * ⚠️ Đây là **workaround**, không phải giải pháp. Xem `docs/stamp-collection/API-MAPPING.md`:
 *   - API #21 thiếu `milestonePosition` + `milestoneType`
 *   - API #17/#18/#22 thiếu `milestonePosition`
 *   - API #4 thiếu `discountType`
 * Nếu BE bổ sung các field này thì bỏ hẳn service này và xoá phần join ở 3 service kia.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminChallengeMilestoneIndexService {

  private milestoneCache = new Map<string, Map<string, MilestoneIndexEntry>>()
  private rewardOptionCache = new Map<string, Map<string, RewardOptionIndexEntry>>()
  /** Gộp các lần gọi API #10 đồng thời cho cùng một Challenge thành 1 request. */
  private pendingLoads = new Map<string, Promise<void>>()

  constructor(private challengeService: AdminChallengeService) {}

  /** Map `milestoneId → {position, type, name}` của một Challenge. */
  public async get(challengeId?: string): Promise<Map<string, MilestoneIndexEntry>> {
    if (!challengeId) {
      return new Map()
    }

    if (this.milestoneCache.has(challengeId)) {
      return this.milestoneCache.get(challengeId)
    }

    await this.load(challengeId)

    return this.milestoneCache.get(challengeId) || new Map()
  }

  /** Map `rewardOptionId → {tierPosition, type, discountType, name}` của một Challenge. */
  public async getRewardOptions(
    challengeId?: string
  ): Promise<Map<string, RewardOptionIndexEntry>> {
    if (!challengeId) {
      return new Map()
    }

    if (this.rewardOptionCache.has(challengeId)) {
      return this.rewardOptionCache.get(challengeId)
    }

    await this.load(challengeId)

    return this.rewardOptionCache.get(challengeId) || new Map()
  }

  /**
   * Nạp sẵn index từ Challenge mà màn gọi đã tải bằng API #10 (vd Challenge Detail) — các tab
   * bên trong dùng lại, không gọi API #10 lần nữa. Ghi đè cache cũ vì đây là dữ liệu mới nhất.
   */
  public seed(challengeId: string, challenge: ChallengeDto): void {
    if (!challengeId || !challenge) {
      return
    }

    this.buildIndexes(challengeId, challenge)
  }

  /** Gọi lại API #10 sau khi Save Challenge (position / discountType có thể đã đổi). */
  public invalidate(challengeId?: string): void {
    if (!challengeId) {
      this.milestoneCache.clear()
      this.rewardOptionCache.clear()
      return
    }

    this.milestoneCache.delete(challengeId)
    this.rewardOptionCache.delete(challengeId)
  }

  private load(challengeId: string): Promise<void> {
    if (!this.pendingLoads.has(challengeId)) {
      const pending = this.fetchIndexes(challengeId).then(() => {
        this.pendingLoads.delete(challengeId)
      })
      this.pendingLoads.set(challengeId, pending)
    }

    return this.pendingLoads.get(challengeId)
  }

  private async fetchIndexes(challengeId: string): Promise<void> {
    try {
      const challenge = await this.challengeService.getById(challengeId)

      this.buildIndexes(challengeId, challenge)
    } catch (error) {
      // Không chặn luồng chính: mã MS-00x sẽ hiển thị '—', cột Discount fallback về '%'
    }
  }

  private buildIndexes(challengeId: string, challenge: ChallengeDto): void {
    const milestones = new Map<string, MilestoneIndexEntry>()
    const rewardOptions = new Map<string, RewardOptionIndexEntry>()

    ;(challenge?.milestones || []).forEach(milestone => {
      milestones.set(milestone.id, {
        id: milestone.id,
        position: milestone.position,
        type: milestone.type,
        name: milestone.name,
        qrAvailable: milestone.qrAvailable,
        eventId: milestone.eventId
      })
    })

    ;(challenge?.rewardTiers || []).forEach(tier => {
      ;(tier.options || []).forEach(option => {
        rewardOptions.set(option.id, {
          id: option.id,
          tierPosition: tier.position,
          type: option.type,
          discountType: option.discountType,
          name: option.name,
          numberOfRewards: option.numberOfRewards,
          mallIds: option.mallIds || [],
          offlineTenantIds: option.offlineTenantIds || [],
          discountValue: option.discountValue
        })
      })
    })

    this.milestoneCache.set(challengeId, milestones)
    this.rewardOptionCache.set(challengeId, rewardOptions)
  }
}
